import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILeaguesStore} from "data/stores/leagues/leagues.store";
import {ILeague} from "data/types/entities";
import {ILeaguesFilters} from "data/types/api";

export interface IMyLeaguesController extends ViewController {
	searchValue: string;

	loadMore: () => void;

	onFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

	get filters(): ILeaguesFilters;

	get leagues(): ILeague[];

	get isLoading(): boolean;

	get nextPage(): boolean;

	get isLeaguesNotExist(): boolean;
}

@injectable()
export class MyLeaguesController implements IMyLeaguesController {
	@observable searchValue = "";
	@observable searchTimer: ReturnType<typeof setTimeout> | undefined;

	constructor(@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore) {
		makeAutoObservable(this);
	}

	dispose(): void {
		return;
	}

	init(param: void): void {
		this._leaguesStore.clearSelectedLeague();
		this.fetchLeagues();
	}

	private fetchLeagues(): void {
		void this._leaguesStore.fetchMyLeagues();
	}

	get filters(): ILeaguesFilters {
		return this._leaguesStore.leagueFilters;
	}

	get leagues(): ILeague[] {
		return this._leaguesStore.myLeagues;
	}

	get isLeaguesNotExist(): boolean {
		return (
			this._leaguesStore.myLeagues.length === 0 &&
			this.searchValue === "" &&
			!this.searchTimer
		);
	}

	get isLoading(): boolean {
		return this._leaguesStore.isMyLeaguesLoading;
	}

	public loadMore = (): void => {
		void this._leaguesStore.fetchLeaguesLoadMore();
	};

	get nextPage(): boolean {
		return this._leaguesStore.loadMoreMapper.myLeagues;
	}

	private updateFilters = (name: string, value: string) => {
		void this._leaguesStore.updateFiltersForMyLeagues({...this.filters, [name]: value});
	};

	public onFieldChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const {name, value} = event.currentTarget;

		if (!name) {
			return;
		}

		this.searchValue = value;
		clearTimeout(this.searchTimer);
		this.searchTimer = setTimeout(() => {
			this.updateFilters(name, value);
		}, 500);
	};
}
